import { useState, useEffect, useRef } from "react";

/* ── THEME ─────────────────────────────────────────────────────────────────── */
const T = {
  bg: "#f8fafc", card: "#ffffff", border: "#e2e8f0",
  text: "#0f172a", muted: "#475569", dim: "#94a3b8",
  blue: { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  green: { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  red: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  amber: { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
  purple: { bg: "#ede9fe", text: "#5b21b6", border: "#c4b5fd" },
  teal: { bg: "#ccfbf1", text: "#0e7490", border: "#5eead4" },
  orange: { bg: "#ffedd5", text: "#9a3412", border: "#fdba74" },
};

/* ── SHARED COMPONENTS ──────────────────────────────────────────────────────── */
const Tag = ({ color = T.blue, children }) => (
  <span style={{ display:"inline-flex", alignItems:"center", padding:"2px 10px", borderRadius:20,
    background: color.bg, color: color.text, fontSize:11, fontWeight:700,
    letterSpacing:"0.05em", textTransform:"uppercase", border:`1px solid ${color.border}` }}>
    {children}
  </span>
);

const Card = ({ children, style = {} }) => (
  <div style={{ background: T.card, border:`1px solid ${T.border}`, borderRadius:12,
    padding:"20px 24px", marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,0.04)", ...style }}>
    {children}
  </div>
);

const SectionLabel = ({ color = T.blue, children }) => (
  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
    <div style={{ width:4, height:28, borderRadius:2, background: color.text }} />
    <span style={{ fontFamily:"Georgia,serif", fontSize:13, fontWeight:700,
      color: color.text, letterSpacing:"0.08em", textTransform:"uppercase" }}>
      {children}
    </span>
  </div>
);

const HR = () => <div style={{ height:1, background: T.border, margin:"32px 0" }} />;

function ScoreRing({ score, total, color = T.blue }) {
  const pct = total ? score / total : 0;
  const r = 28, circ = 2 * Math.PI * r;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <svg width={70} height={70}>
        <circle cx={35} cy={35} r={r} fill="none" stroke={T.border} strokeWidth={6} />
        <circle cx={35} cy={35} r={r} fill="none" stroke={color.text} strokeWidth={6}
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round" transform="rotate(-90 35 35)"
          style={{ transition:"stroke-dashoffset 0.6s ease" }} />
        <text x={35} y={40} textAnchor="middle" fontSize={15} fontWeight={700} fill={T.text}>
          {score}/{total}
        </text>
      </svg>
      <span style={{ fontSize:11, color: T.muted, fontWeight:600 }}>
        {total ? Math.round(pct * 100) : 0}%
      </span>
    </div>
  );
}

/* ── REVEAL HOOK ────────────────────────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}
function Reveal({ children, delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(18px)",
      transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 1 – THE HOLY TRINITY
══════════════════════════════════════════════════════════════════════════════ */
function Section1() {
  const [step, setStep] = useState(0);
  const steps = [
    { label:"Objectives & Needs", icon:"🎯", subtitle:"The Destination", color: T.blue,
      desc:"What is the PURPOSE of this money? The goal that drives all asset selection.",
      items:["Capital Preservation – 'Do not lose my starting money' (GICs, T-Bills, MMFs)",
        "Income – 'Pay my monthly bills' (Bonds, Preferred Shares, REITs, Blue-Chip Dividends)",
        "Growth – 'Beat inflation over the long term' (Common Shares, Equity ETFs)",
        "Speculation – 'Home run / 10-bagger' — only for a tiny slice of play money"] },
    { label:"Risk Profile", icon:"⚖️", subtitle:"The Vehicle & Driver", color: T.purple,
      desc:"The single most important constraint. Risk Profile ALWAYS WINS when it conflicts with Objective.",
      items:["Risk Tolerance (Stomach) – Emotional willingness to watch money fluctuate",
        "Risk Capacity (Wallet) – Financial ability to withstand a loss (math: income, net worth, time horizon)",
        "Risk Need (Required Speed) – The mathematical return needed to achieve the stated goal",
        "GOLDEN RULE: The overall profile = the LOWER of Tolerance vs Capacity"] },
    { label:"Performance", icon:"📊", subtitle:"The Journey", color: T.teal,
      desc:"Good performance is NOT the highest number. It is achieving the Objective within the Risk Profile.",
      items:["'Successful Failure': A 30% gain on a High-Risk fund for a Low-Risk Income client is a suitability FAILURE",
        "'Boring Success': An 8% return on a Conservative Fund vs a 25% market is A+ performance for a Low-Risk client",
        "The correct benchmark must match the client's actual risk profile, not the broad market",
        "RR's job: manage expectations BEFORE the comparison is made, not after"] },
  ];
  return (
    <Reveal>
      <SectionLabel color={T.blue}>7.1 — The Holy Trinity</SectionLabel>
      <Card style={{ background:"linear-gradient(135deg,#eff6ff,#f0fdf4)", border:`1px solid ${T.blue.border}` }}>
        <p style={{ margin:0, fontSize:14, color: T.text, fontStyle:"italic", lineHeight:1.7 }}>
          <strong>Analogy: The Financial Navigator.</strong> Objectives = Destination. Risk Profile = Vehicle & Driver. Performance = Journey.
          Your job: ensure all three are in <em>perfect alignment</em>. Failing to align = suitability violation.
        </p>
      </Card>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
        {steps.map((s,i) => (
          <button key={i} onClick={() => setStep(i)}
            style={{ padding:"14px 12px", borderRadius:10, border:`2px solid ${step===i ? s.color.text : T.border}`,
              background: step===i ? s.color.bg : T.card, cursor:"pointer", textAlign:"left", transition:"all 0.2s" }}>
            <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
            <div style={{ fontFamily:"Georgia,serif", fontWeight:700, fontSize:13, color: T.text }}>{s.label}</div>
            <div style={{ fontSize:11, color: T.muted, marginTop:2 }}>{s.subtitle}</div>
          </button>
        ))}
      </div>
      {(() => { const s = steps[step]; return (
        <Card style={{ border:`1.5px solid ${s.color.border}`, background: s.color.bg }}>
          <p style={{ fontSize:13, color: s.color.text, fontWeight:600, marginBottom:12, marginTop:0 }}>{s.desc}</p>
          <ul style={{ margin:0, paddingLeft:20 }}>
            {s.items.map((it,i) => (
              <li key={i} style={{ fontSize:13, color: T.text, marginBottom:6, lineHeight:1.6 }}>{it}</li>
            ))}
          </ul>
        </Card>
      );})()}
      <Card style={{ border:`1px solid ${T.amber.border}`, background: T.amber.bg }}>
        <strong style={{ color: T.amber.text, fontSize:13 }}>⚡ Critical Conflicts</strong>
        <div style={{ marginTop:10, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          {[
            ["High Tolerance + Low Capacity", "Assign LOW risk — capacity vetoes tolerance"],
            ["Low Tolerance + High Capacity", "Assign LOW risk — tolerance vetoes capacity (can't force risk)"],
            ["Aggressive Barista", "25-yr-old with -$20k net worth → Low Risk despite High Tolerance"],
            ["Timid Millionaire", "$10M in GICs terrified of markets → Low Risk despite High Capacity"],
          ].map(([q,a],i) => (
            <div key={i} style={{ background: T.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:12, fontWeight:700, color: T.text, marginBottom:4 }}>{q}</div>
              <div style={{ fontSize:12, color: T.muted }}>{a}</div>
            </div>
          ))}
        </div>
      </Card>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 2 – NON-FINANCIAL CONSTRAINTS
══════════════════════════════════════════════════════════════════════════════ */
function Section2() {
  const [active, setActive] = useState(null);
  const constraints = [
    { id:0, icon:"🌱", label:"ESG Investing", tag:"Environmental / Social / Governance", color: T.green,
      impact:"Negative Screen: Excludes 'bad' companies (oil & gas, tobacco, firearms). Positive Screen: Seeks 'good' companies (clean energy, EDI leaders). TRACKING ERROR — portfolio diverges from broad benchmark in years when excluded sectors outperform.",
      duty:"Identify, Document, Analyze impact (diversification loss, potential underperformance vs TSX when Energy sector leads), Propose suitable solution (ESG-screened ETF)." },
    { id:1, icon:"⚖️", label:"EDI Constraints", tag:"Equity, Diversity & Inclusion", color: T.purple,
      impact:"Positive screen for companies with strong female leadership / diverse boards. Typically uses niche ETFs — LESS DIVERSIFIED (e.g., 50 vs 500 stocks), HIGHER MER (0.50% vs 0.05%), different performance from broad market.",
      duty:"Disclose the trade-off: 'This EDI ETF is less diversified and more expensive. Are you comfortable with this trade-off to align money with values?'" },
    { id:2, icon:"🕌", label:"Religious Constraints", tag:"e.g., Sharia-Compliant", color: T.teal,
      impact:"Prohibits: Interest (riba) — ENTIRE Fixed Income asset class is unsuitable. Haram industries: alcohol, tobacco, gambling, pork, firearms. Banks (earn interest) are also excluded → Cannot use TSX Index (30% financials).",
      duty:"Use special sukuk products for fixed income. Avoid Financials sector. Highly customized portfolio. Performance will differ significantly from benchmark." },
    { id:3, icon:"💝", label:"Loyalty / Emotional Constraints", tag:"Family Stock / 'I hate that company'", color: T.amber,
      impact:"Client refuses to sell for emotional reasons. Classic: 90% concentration in one inherited 'family stock'. Portfolio = dangerously unsuitable despite client's stated 'Balanced' risk profile.",
      duty:"ADVISE on record of extreme concentration risk. DOCUMENT client's informed refusal in writing. MANAGE the remaining 10%. You CANNOT force a sale." },
  ];
  return (
    <Reveal delay={60}>
      <SectionLabel color={T.green}>7.2 — Non-Financial Constraints</SectionLabel>
      <Card style={{ background: T.green.bg, border:`1px solid ${T.green.border}` }}>
        <p style={{ margin:0, fontSize:13, color: T.green.text, lineHeight:1.7 }}>
          Under <strong>Client Focused Reforms (CFRs)</strong>, ignoring a client's ethical or personal constraint is just as much a suitability violation as recommending a high-risk stock to a low-risk client. These are <em>non-negotiable</em>.
        </p>
      </Card>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12, marginBottom:16 }}>
        {constraints.map(c => (
          <button key={c.id} onClick={() => setActive(active===c.id ? null : c.id)}
            style={{ padding:"14px 16px", borderRadius:10, border:`2px solid ${active===c.id ? c.color.text : T.border}`,
              background: active===c.id ? c.color.bg : T.card, cursor:"pointer", textAlign:"left", transition:"all 0.2s" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:20 }}>{c.icon}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color: T.text }}>{c.label}</div>
                <div style={{ fontSize:11, color: T.muted }}>{c.tag}</div>
              </div>
            </div>
            {active === c.id && (
              <div style={{ marginTop:10, borderTop:`1px solid ${c.color.border}`, paddingTop:10 }}>
                <p style={{ margin:"0 0 8px", fontSize:12, color: T.text, lineHeight:1.7 }}><strong>Impact:</strong> {c.impact}</p>
                <p style={{ margin:0, fontSize:12, color: c.color.text, lineHeight:1.7 }}><strong>RR's Duty:</strong> {c.duty}</p>
              </div>
            )}
          </button>
        ))}
      </div>
      <Card style={{ border:`1px solid ${T.purple.border}`, background: T.purple.bg }}>
        <strong style={{ color: T.purple.text, fontSize:13 }}>Screening Types: Negative vs Positive</strong>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:10 }}>
          <div style={{ background: T.card, borderRadius:8, padding:"10px 12px" }}>
            <div style={{ fontWeight:700, fontSize:12, color: T.red.text, marginBottom:4 }}>🚫 Negative Screen</div>
            <div style={{ fontSize:12, color: T.muted }}>EXCLUDES 'bad' companies. E.g., no oil & gas, no tobacco, no firearms.</div>
          </div>
          <div style={{ background: T.card, borderRadius:8, padding:"10px 12px" }}>
            <div style={{ fontWeight:700, fontSize:12, color: T.green.text, marginBottom:4 }}>✅ Positive Screen (Impact Investing)</div>
            <div style={{ fontSize:12, color: T.muted }}>ACTIVELY SEEKS 'good' companies solving climate change, advancing EDI, etc.</div>
          </div>
        </div>
      </Card>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 3 – BEHAVIOURAL FINANCE + GAME
══════════════════════════════════════════════════════════════════════════════ */
const BIASES = [
  { name:"Representativeness", cat:"Cognitive", color: T.blue,
    clue:"'This tech startup CEO is a college dropout — it's the NEXT AMAZON!'",
    fix:"Look past the story. Do fundamental analysis on THIS company's balance sheet, not the pattern." },
  { name:"Illusion of Control", cat:"Cognitive", color: T.blue,
    clue:"'I check my portfolio 10 times a day — I KNOW when to get in and out.'",
    fix:"90% of active traders underperform buy-and-hold. Use a core 90% index + 10% active satellite." },
  { name:"Confirmation Bias", cat:"Cognitive", color: T.blue,
    clue:"'I ONLY listen to the one blog that agrees with me. The 4 analysts saying Sell are wrong.'",
    fix:"'My professional duty is objective facts — this Balance Sheet shows debt > assets. That is a fact, not opinion.'" },
  { name:"Hindsight Bias", cat:"Cognitive", color: T.blue,
    clue:"'I KNEW the market was going to crash — that's why I sold!'  (But they panic-sold at the bottom.)",
    fix:"Actions reveal the truth. Panic-selling proves you didn't know. Learn: 'I am bad at market timing.'" },
  { name:"Framing", cat:"Info Processing", color: T.purple,
    clue:"A 3% GIC is pitched as 'LOSING 2% to inflation' (negative frame) to manipulate a conservative client.",
    fix:"Present both frames neutrally: 'Guaranteed 3% return' AND 'potential inflation lag.' Let client decide." },
  { name:"Anchoring", cat:"Info Processing", color: T.purple,
    clue:"'TechCo IPO'd at $100. It's now $30. It's a SCREAMING BUY — it's a $100 stock on sale!'",
    fix:"The $100 IPO price is irrelevant. Run fresh fundamental analysis on the company as it is TODAY." },
  { name:"Mental Accounting", cat:"Info Processing", color: T.purple,
    clue:"'This $100k INHERITANCE is 'found money' — I'll gamble it all on crypto. My real retirement money is safe.'",
    fix:"ALL money is YOUR money. A $100k loss WILL impact your retirement. No 'house money' bucket exists." },
  { name:"Availability Bias", cat:"Info Processing", color: T.purple,
    clue:"'The ARK Fund is #1 on the news! I MUST buy it. It's all I hear about!'",
    fix:"Look at the 10-year risk, not just the 1-year headline return. Is this volatility suitable for your KYC?" },
  { name:"Loss Aversion", cat:"Emotional", color: T.red,
    clue:"Portfolio is down 20%. 'SELL EVERYTHING TO CASH. The pain is too much. I just want it to stop.'",
    fix:"'This is the storm we planned for. Selling NOW turns a PAPER loss into a PERMANENT loss.'" },
  { name:"Overconfidence", cat:"Emotional", color: T.red,
    clue:"Client's stock doubled (once). 'My GUT beats your diversification plan. Put 100% in TechCo 2.0!'",
    fix:"'One win is often luck. Let's protect the gain by rebalancing — sell half, diversify back into core.'" },
  { name:"Endowment Bias", cat:"Emotional", color: T.red,
    clue:"'I CAN'T sell this stock — it's been in my family 40 years. It's a GOOD, SAFE company.'",
    fix:"'If you had $1M cash today, would you BUY this specific company? If no, we need a plan to diversify.'" },
  { name:"Survivorship Bias", cat:"Capital Markets", color: T.teal,
    clue:"'70% of mutual funds BEAT the index!' (They only looked at surviving funds; 2,000 failures were excluded.)",
    fix:"Real success rate = winners / ALL funds that started. Always ask: 'How many funds closed during this period?'" },
  { name:"Gambler's Fallacy", cat:"Capital Markets", color: T.teal,
    clue:"'The market has been DOWN 6 months — it's DUE for a rally! I must buy now!'",
    fix:"Markets have no memory. Past 6 months are irrelevant. Stick to Strategic Asset Allocation, not predictions." },
  { name:"Herding", cat:"Capital Markets", color: T.teal,
    clue:"'My dentist, my neighbour, EVERYONE is making millions in these tech stocks. I CAN'T miss out!'",
    fix:"FOMO at a bubble peak is the most dangerous time to invest. 'This is unsuitable — you're following the crowd, not facts.'" },
  { name:"Home Country Bias", cat:"Capital Markets", color: T.teal,
    clue:"'I'm 100% in Canadian stocks. I KNOW Canada — it's safer!'",
    fix:"Canada = only 3% of world's stock market. Sell 50% TSX → add S&P 500 + EAFE for proper diversification." },
];

function Section3() {
  const [gameIdx, setGameIdx] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState({ c:0, w:0 });
  const [openBiases, setOpenBiases] = useState({});

  const cats = [...new Set(BIASES.map(b => b.cat))];
  const catColors = { "Cognitive": T.blue, "Info Processing": T.purple, "Emotional": T.red, "Capital Markets": T.teal };

  function startGame() {
    setGameIdx(Math.floor(Math.random() * BIASES.length));
    setRevealed(false);
  }

  const toggleBias = (biasName) => {
    setOpenBiases(prev => ({ ...prev, [biasName]: !prev[biasName] }));
  };

  return (
    <Reveal delay={80}>
      <SectionLabel color={T.red}>7.3 — Behavioural Finance</SectionLabel>
      <Card style={{ background:"linear-gradient(135deg,#fef2f2,#fdf4ff)", border:`1px solid ${T.red.border}` }}>
        <p style={{ margin:0, fontSize:13, color: T.text, lineHeight:1.7 }}>
          <strong>Core insight:</strong> Investors are NOT rational. Your greatest value as an RR is being a <em>behavioural coach</em> — identifying biases and protecting clients from their own worst instincts.
          Biases are grouped into: <strong>Cognitive</strong> (fixable with data) | <strong>Information Processing</strong> (mental shortcuts) | <strong>Emotional</strong> (harder to fix, must manage) | <strong>Capital Markets</strong> (crowd-wide patterns).
        </p>
      </Card>

      {/* Bias Cards Grid */}
      {cats.map(cat => (
        <div key={cat} style={{ marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <Tag color={catColors[cat]}>{cat}</Tag>
            <span style={{ fontSize:12, color: T.muted }}>biases</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px,1fr))", gap:10 }}>
            {BIASES.filter(b => b.cat === cat).map((b,i) => {
              const open = openBiases[b.name];
              return (
                <button key={i} onClick={() => toggleBias(b.name)}
                  style={{ padding:"12px 14px", borderRadius:10, border:`1.5px solid ${open ? b.color.text : T.border}`,
                    background: open ? b.color.bg : T.card, cursor:"pointer", textAlign:"left", transition:"all 0.2s" }}>
                  <div style={{ fontWeight:700, fontSize:13, color: T.text, marginBottom: open ? 8 : 0 }}>{b.name}</div>
                  {open && (
                    <>
                      <div style={{ fontSize:12, color: T.muted, marginBottom:6, fontStyle:"italic", lineHeight:1.6 }}>💬 {b.clue}</div>
                      <div style={{ fontSize:12, color: b.color.text, lineHeight:1.6 }}>✅ <strong>Coach:</strong> {b.fix}</div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Bias Identification Game */}
      <Card style={{ border:`1.5px solid ${T.purple.border}`, background: T.purple.bg }}>
        <strong style={{ color: T.purple.text, fontSize:13 }}>🎮 Bias Spotter Game</strong>
        <p style={{ fontSize:12, color: T.muted, margin:"6px 0 12px" }}>Read the client statement — name the bias!</p>
        {gameIdx === null ? (
          <button onClick={startGame} style={{ padding:"10px 22px", background: T.purple.text, color:"#fff",
            border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:700 }}>Start Game</button>
        ) : (
          <div>
            <div style={{ background: T.card, borderRadius:8, padding:"12px 16px", marginBottom:12, border:`1px solid ${T.border}` }}>
              <p style={{ margin:0, fontSize:13, color: T.text, fontStyle:"italic", lineHeight:1.7 }}>
                "{BIASES[gameIdx].clue}"
              </p>
            </div>
            {!revealed ? (
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {[BIASES[gameIdx].name, ...BIASES.filter((_,i) => i !== gameIdx).sort(() => Math.random()-0.5).slice(0,3).map(b => b.name)].sort(() => Math.random()-0.5).map((name,i) => (
                  <button key={i} onClick={() => { setRevealed(true); if(name===BIASES[gameIdx].name) setScore(s=>({...s,c:s.c+1})); else setScore(s=>({...s,w:s.w+1})); }}
                    style={{ padding:"8px 14px", borderRadius:8, border:`1.5px solid ${T.border}`, background: T.card,
                      cursor:"pointer", fontSize:12, fontWeight:600, color: T.text }}>
                    {name}
                  </button>
                ))}
              </div>
            ) : (
              <div>
                <div style={{ background: T.green.bg, border:`1px solid ${T.green.border}`, borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
                  <strong style={{ color: T.green.text, fontSize:13 }}>✓ {BIASES[gameIdx].name}</strong>
                  <p style={{ margin:"4px 0 0", fontSize:12, color: T.text }}>Coach response: {BIASES[gameIdx].fix}</p>
                </div>
                <button onClick={startGame} style={{ padding:"8px 18px", background: T.purple.text, color:"#fff",
                  border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:700 }}>Next →</button>
              </div>
            )}
            <div style={{ marginTop:10, fontSize:12, color: T.muted }}>
              Score: <strong style={{ color: T.green.text }}>{score.c} ✓</strong> &nbsp; <strong style={{ color: T.red.text }}>{score.w} ✗</strong>
            </div>
          </div>
        )}
      </Card>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 4 – SUITABILITY & PORTFOLIO PROCESS
══════════════════════════════════════════════════════════════════════════════ */
function Section4() {
  const checklist = [
    { label:"Objective Match", q:"Does it provide Income/Growth/etc. as needed?", pass:"Bond/REIT for Income client", fail:"Growth stock for Income retiree" },
    { label:"Risk Profile Match", q:"Is the risk level appropriate?", pass:"GIC for Low-Risk client", fail:"High-Risk ETF for Low-Risk client" },
    { label:"Time Horizon Match", q:"Is it liquid enough for their timeline?", pass:"6-month GIC for 1-year goal", fail:"10-year bond for 6-month need" },
    { label:"Knowledge Match", q:"Can the client understand this product?", pass:"All-in-one Balanced Fund for novice", fail:"Leveraged ETF for novice investor" },
    { label:"Tax Match", q:"Is it tax-efficient in this account type?", pass:"T-SWP for OAS-sensitive retiree", fail:"Interest fund in taxable account for high-income client" },
    { label:"Constraint Match", q:"Does it violate any ESG/religious/personal rule?", pass:"ESG-screened ETF for environmentalist", fail:"TSX Index ETF for client who excluded Energy" },
  ];
  const [activeRow, setActiveRow] = useState(null);
  return (
    <Reveal delay={100}>
      <SectionLabel color={T.amber}>7.4–7.7 — Suitability & Portfolio Process</SectionLabel>
      <Card style={{ background: T.amber.bg, border:`1px solid ${T.amber.border}` }}>
        <strong style={{ color: T.amber.text, fontSize:13 }}>The Suitability Checklist: Every product must pass ALL six tests</strong>
      </Card>
      <div style={{ marginBottom:16 }}>
        {checklist.map((row,i) => (
          <button key={i} onClick={() => setActiveRow(activeRow===i ? null : i)}
            style={{ width:"100%", padding:"12px 16px", marginBottom:4, borderRadius:8,
              border:`1.5px solid ${activeRow===i ? T.amber.text : T.border}`,
              background: activeRow===i ? T.amber.bg : T.card, cursor:"pointer", textAlign:"left", transition:"all 0.2s",
              display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:16 }}>{activeRow===i ? "🔍" : "☑️"}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:13, color: T.text }}>{row.label}</div>
              {activeRow===i && (
                <div style={{ marginTop:8, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <div style={{ background: T.green.bg, borderRadius:6, padding:"8px 10px" }}>
                    <div style={{ fontSize:11, fontWeight:700, color: T.green.text, marginBottom:3 }}>✓ PASS Example</div>
                    <div style={{ fontSize:11, color: T.text }}>{row.pass}</div>
                  </div>
                  <div style={{ background: T.red.bg, borderRadius:6, padding:"8px 10px" }}>
                    <div style={{ fontSize:11, fontWeight:700, color: T.red.text, marginBottom:3 }}>✗ FAIL Example</div>
                    <div style={{ fontSize:11, color: T.text }}>{row.fail}</div>
                  </div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <Card>
          <strong style={{ fontSize:13, color: T.text }}>💰 Cash Management Priorities</strong>
          <ol style={{ margin:"10px 0 0", paddingLeft:20 }}>
            {["Pay off HIGH-INTEREST debt first (22% credit card = guaranteed 22% return — nothing beats it)",
              "Build Emergency Fund: 3-6 months expenses in HISA/Money Market BEFORE any market investment",
              "Start 'Pay Yourself First' via Pre-Authorized Contribution (PAC): automates savings habit",
              "Maximize RRSP/TFSA before non-registered accounts"].map((it,i) => (
              <li key={i} style={{ fontSize:12, color: T.muted, marginBottom:6, lineHeight:1.6 }}>{it}</li>
            ))}
          </ol>
        </Card>
        <Card>
          <strong style={{ fontSize:13, color: T.text }}>🏛️ Government Pensions (CPP / OAS)</strong>
          <div style={{ marginTop:10 }}>
            {[
              { l:"CPP", d:"Work-based, not income-tested. Take early at 60 = -36%. Delay to 70 = +42% guaranteed for life. Best advice for healthy HNW client: DELAY to 70." },
              { l:"OAS", d:"Residency-based. CLAWBACK starts at ~$91k net income, fully lost at ~$148k. CRITICAL: Recommending a high-interest bond fund for an $130k-income retiree can trigger the full OAS clawback — a catastrophic suitability failure." },
            ].map((g,i) => (
              <div key={i} style={{ marginBottom:8, padding:"8px 10px", borderRadius:6,
                background: i===0 ? T.blue.bg : T.red.bg, border:`1px solid ${i===0 ? T.blue.border : T.red.border}` }}>
                <strong style={{ fontSize:12, color: i===0 ? T.blue.text : T.red.text }}>{g.l}</strong>
                <p style={{ margin:"4px 0 0", fontSize:11, color: T.text, lineHeight:1.6 }}>{g.d}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 5 – TAX PLANNING (TFSA / RRSP / FHSA / RESP / Corporate)
══════════════════════════════════════════════════════════════════════════════ */
function Section5() {
  const [taxView, setTaxView] = useState("accounts");
  const [openAccounts, setOpenAccounts] = useState({});
  
  const toggleAccount = (accountName) => {
    setOpenAccounts(prev => ({ ...prev, [accountName]: !prev[accountName] }));
  };
  
  const accounts = [
    { name:"TFSA", color: T.green, tag:"Tax-Free", contribution:"$7,000/yr (2024)", withdrawal:"Tax-free any time", bestFor:"Highest-taxed assets FIRST (Interest, Foreign Dividends)", trap:"Foreign dividends lose withholding tax relief inside TFSA" },
    { name:"RRSP", color: T.blue, tag:"Tax-Deferred", contribution:"18% of prior yr income (max ~$31,560)", withdrawal:"Taxed as income (converted to RRIF by 71)", bestFor:"High-income earners now, lower income in retirement", trap:"Converting RRSP income could trigger OAS clawback" },
    { name:"FHSA", color: T.purple, tag:"First Home Savings", contribution:"$8,000/yr, $40,000 lifetime", withdrawal:"Tax-free for qualifying first home purchase", bestFor:"First-time buyers: RRSP deduction + TFSA withdrawal", trap:"Must use within 15 years or convert to RRSP" },
    { name:"RESP", color: T.teal, tag:"Education", contribution:"$2,500/yr for maximum CESG ($500 grant = 20%)", withdrawal:"EAPs taxed in student's hands (low/nil tax)", bestFor:"20% guaranteed CESG return — best 'return' available", trap:"Non-educational withdrawal forfeits grants; growth taxed back to subscriber" },
    { name:"Corporate Account", color: T.amber, tag:"Professional Corp", contribution:"Passive income taxed at ~50% (disincentive rate)", withdrawal:"CDA: non-taxable half of capital gains → tax-free dividend to owner", bestFor:"Capital gains (CDA strategy). AVOID: interest income", trap:"Interest income = worst tax outcome inside a corporation" },
  ];
  const assetLocation = [
    { asset:"Interest Income (GIC / Bond)", tax:"100% taxable — WORST", shelter:"TFSA first, then RRSP" },
    { asset:"Foreign Dividends (US stocks)", tax:"100% taxable (no DTC)", shelter:"RRSP preferred (treaty relief), TFSA second" },
    { asset:"Canadian Eligible Dividends", tax:"Gross-up + DTC — LOW effective rate", shelter:"Non-registered OK — already tax-efficient" },
    { asset:"Capital Gains", tax:"50% inclusion — MODERATE", shelter:"Non-registered OK (deferred until sale, tax-loss harvest)" },
    { asset:"Return of Capital (T-SWP)", tax:"Tax-deferred — BEST for income needs", shelter:"Non-registered ideal (reduces ACB, defers tax)" },
  ];
  return (
    <Reveal delay={120}>
      <SectionLabel color={T.purple}>7.9 — Tax Planning for Investments</SectionLabel>
      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        {[["accounts","Registered Accounts"],["location","Asset Location"]].map(([v,l]) => (
          <button key={v} onClick={() => setTaxView(v)}
            style={{ padding:"8px 18px", borderRadius:20, border:`1.5px solid ${taxView===v ? T.purple.text : T.border}`,
              background: taxView===v ? T.purple.bg : T.card, cursor:"pointer", fontSize:12,
              fontWeight:700, color: taxView===v ? T.purple.text : T.muted }}>
            {l}
          </button>
        ))}
      </div>
      {taxView === "accounts" ? (
        <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:10 }}>
          {accounts.map((a,i) => {
            const open = openAccounts[a.name];
            return (
              <button key={i} onClick={() => toggleAccount(a.name)}
                style={{ padding:"12px 16px", borderRadius:10, border:`1.5px solid ${open ? a.color.text : T.border}`,
                  background: open ? a.color.bg : T.card, cursor:"pointer", textAlign:"left", transition:"all 0.2s" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: open ? 10 : 0 }}>
                  <Tag color={a.color}>{a.tag}</Tag>
                  <span style={{ fontWeight:700, fontSize:14, color: T.text }}>{a.name}</span>
                </div>
                {open && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:4 }}>
                    {[["Contribution Room", a.contribution],["Withdrawal", a.withdrawal],["Best For", a.bestFor],["⚠️ Trap", a.trap]].map(([k,v],j) => (
                      <div key={j} style={{ background: T.card, borderRadius:6, padding:"8px 10px", border:`1px solid ${T.border}` }}>
                        <div style={{ fontSize:11, fontWeight:700, color: T.muted, marginBottom:3 }}>{k}</div>
                        <div style={{ fontSize:12, color: T.text }}>{v}</div>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background: T.purple.bg }}>
                {["Asset Type","Tax Treatment","Best Account Location"].map(h => (
                  <th key={h} style={{ padding:"10px 12px", textAlign:"left", color: T.purple.text, fontWeight:700, borderBottom:`2px solid ${T.purple.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assetLocation.map((row,i) => (
                <tr key={i} style={{ background: i%2===0 ? T.bg : T.card, borderBottom:`1px solid ${T.border}` }}>
                  <td style={{ padding:"10px 12px", fontWeight:600, color: T.text }}>{row.asset}</td>
                  <td style={{ padding:"10px 12px", color: T.muted }}>{row.tax}</td>
                  <td style={{ padding:"10px 12px", color: T.green.text, fontWeight:600 }}>{row.shelter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 6 – CAPITAL GAINS + INCOME TAX GAME
══════════════════════════════════════════════════════════════════════════════ */
function Section6() {
  const [calc, setCalc] = useState({ pp:70, pShares:100, pComm:15, bp:30, bShares:100, bComm:10, margRate:40 });
  const proceeds = calc.pp * calc.pShares - calc.pComm;
  const acb = calc.bp * calc.bShares + calc.bComm;
  const capitalGain = proceeds - acb;
  const taxableGain = capitalGain * 0.5;
  const taxOwed = taxableGain * (calc.margRate / 100);

  const divTypes = [
    { type:"Eligible Dividends (Canadian public co.)", grossUp:"38%", credit:"Large DTC (federal 15.02%)", effective:"Lowest — often 5–20% effective", note:"Best for non-registered income clients" },
    { type:"Non-Eligible Dividends (CCPC / small biz)", grossUp:"15%", credit:"Smaller DTC (federal 9.03%)", effective:"Medium — lower than interest", note:"From private Canadian companies" },
    { type:"Foreign Dividends (US stocks etc.)", grossUp:"None (treated as interest)", credit:"None (but claim Foreign Tax Credit for 15% withholding)", effective:"Highest — 100% at marginal rate", note:"Worst in non-registered; use RRSP for treaty relief" },
    { type:"Interest Income (GICs, Bonds)", grossUp:"N/A", credit:"None", effective:"Highest — 100% at marginal rate", note:"Shelter in TFSA or RRSP first" },
  ];
  return (
    <Reveal delay={140}>
      <SectionLabel color={T.teal}>7.10–7.11 — Capital Gains & Income Tax</SectionLabel>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {/* Capital Gains Calculator */}
        <Card>
          <strong style={{ fontSize:13, color: T.text }}>🧮 Capital Gains Calculator</strong>
          <div style={{ marginTop:12, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {[["Selling Price/Share","pp"],["Shares Sold","pShares"],["Sell Commission","pComm"],
              ["Buy Price/Share","bp"],["Shares Bought","bShares"],["Buy Commission","bComm"],["Marginal Rate %","margRate"]
            ].map(([label,key]) => (
              <div key={key}>
                <div style={{ fontSize:11, color: T.muted, marginBottom:3 }}>{label}</div>
                <input type="number" value={calc[key]}
                  onChange={e => setCalc(c => ({...c, [key]: Number(e.target.value)}))}
                  style={{ width:"100%", padding:"6px 8px", borderRadius:6, border:`1px solid ${T.border}`,
                    fontSize:12, background: T.bg, color: T.text, boxSizing:"border-box" }} />
              </div>
            ))}
          </div>
          <div style={{ marginTop:12, padding:"10px 12px", borderRadius:8, background: capitalGain >= 0 ? T.green.bg : T.red.bg,
            border:`1px solid ${capitalGain >= 0 ? T.green.border : T.red.border}` }}>
            {[["Proceeds of Disposition", `$${proceeds.toFixed(2)}`],
              ["Adjusted Cost Base", `$${acb.toFixed(2)}`],
              ["Capital Gain / Loss", `$${capitalGain.toFixed(2)}`],
              ["Taxable Gain (50%)", `$${taxableGain.toFixed(2)}`],
              ["Tax Owed", `$${taxOwed.toFixed(2)}`]
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                <span style={{ color: T.muted }}>{k}</span>
                <strong style={{ color: T.text }}>{v}</strong>
              </div>
            ))}
          </div>
        </Card>
        {/* Tax Loss Harvesting */}
        <Card>
          <strong style={{ fontSize:13, color: T.text }}>✂️ Tax-Loss Harvesting Rules</strong>
          <div style={{ marginTop:10 }}>
            {[
              { icon:"✅", label:"Offset Strategy", text:"Sell losing position before Dec 31 to realize capital loss, which offsets capital gains made that year." },
              { icon:"⚠️", label:"Superficial Loss Rule", text:"If YOU (or affiliated person: spouse, RRSP, TFSA, controlled corp) buy IDENTICAL security within 30 days before or after the sale — CRA disallows the loss. 61-day window total." },
              { icon:"✅", label:"Workaround", text:"Wait 31 days to rebuy, OR immediately buy a SIMILAR (not identical) security to stay invested." },
              { icon:"🎁", label:"Donate Appreciated Securities 'In-Kind'", text:"Transfer shares directly to charity. Capital gain is ELIMINATED (0% inclusion rate) AND you get the full donation receipt. Double tax win!" },
            ].map((r,i) => (
              <div key={i} style={{ display:"flex", gap:8, marginBottom:8, padding:"8px 10px", borderRadius:6,
                background: T.bg, border:`1px solid ${T.border}` }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{r.icon}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:12, color: T.text }}>{r.label}</div>
                  <div style={{ fontSize:11, color: T.muted, lineHeight:1.6, marginTop:2 }}>{r.text}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding:"8px 12px", borderRadius:6, background: T.blue.bg, border:`1px solid ${T.blue.border}` }}>
            <strong style={{ fontSize:11, color: T.blue.text }}>Capital Loss Carry Rules:</strong>
            <div style={{ fontSize:11, color: T.text, marginTop:3 }}>
              <strong>Carry back 3 years</strong> (file T1A for refund on prior gains) | <strong>Carry forward indefinitely</strong>
            </div>
          </div>
        </Card>
      </div>

      <strong style={{ fontSize:13, color: T.text, display:"block", marginBottom:10 }}>Dividend Tax Treatment Comparison</strong>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ background: T.teal.bg }}>
              {["Type","Gross-Up","DTC Credit","Effective Rate","Key Note"].map(h => (
                <th key={h} style={{ padding:"9px 10px", textAlign:"left", color: T.teal.text, fontWeight:700, borderBottom:`2px solid ${T.teal.border}`, whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {divTypes.map((r,i) => (
              <tr key={i} style={{ background: i%2===0 ? T.bg : T.card, borderBottom:`1px solid ${T.border}` }}>
                <td style={{ padding:"9px 10px", fontWeight:600, color: T.text }}>{r.type}</td>
                <td style={{ padding:"9px 10px", color: T.muted }}>{r.grossUp}</td>
                <td style={{ padding:"9px 10px", color: T.muted }}>{r.credit}</td>
                <td style={{ padding:"9px 10px", color: i<2 ? T.green.text : T.red.text, fontWeight:700 }}>{r.effective}</td>
                <td style={{ padding:"9px 10px", color: T.muted }}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 7 – COMPLAINTS HANDLING
══════════════════════════════════════════════════════════════════════════════ */
function Section7() {
  const steps = [
    { day:"Day 0", title:"Recognition + Escalation", color: T.blue,
      items:["ANY verbal OR written allegation of misconduct = complaint. Do NOT wait for written form.",
        "Key trigger words: 'misled me', 'unsuitable', 'unauthorized', 'where did my money go'",
        "RR Script: 'I take this very seriously. I must escalate to my Branch Manager and Compliance immediately.'",
        "DO NOT: apologize, admit fault, argue, defend, offer to fix it personally, discourage filing"] },
    { day:"Day 1-5", title:"Acknowledgement Letter", color: T.purple,
      items:["Firm must send formal Acknowledgement Letter within 5 BUSINESS DAYS",
        "Must include: DCO contact info, CIRO 'How to Make a Complaint' brochure, explanation of 90-day process",
        "Must include the CIRO brochure — this is mandatory, not optional"] },
    { day:"Day 6-89", title:"Investigation", color: T.amber,
      items:["Compliance / DCO conducts fair and thorough investigation",
        "RR must provide formal written statement",
        "All documents pulled: KYC forms, trade tickets, client notes, emails"] },
    { day:"Day 90", title:"Substantive Response Letter", color: T.green,
      items:["Firm's FINAL answer must arrive by Day 90",
        "Must include: summary of complaint, investigation results, firm's final decision",
        "CRITICAL: Must explain client's next steps if not satisfied (OBSI + Arbitration)",
        "Report everything to CIRO via ComSet (Complaints and Settlement Reporting System)"] },
  ];
  const resolution = [
    { name:"OBSI", full:"Ombudsman for Banking Services and Investments", binding:"NON-binding", cost:"FREE for client", limit:"Recommends up to $350,000", note:"Firm can refuse but faces public naming/shaming — powerful deterrent" },
    { name:"CIRO Arbitration", full:"Private arbitration process", binding:"LEGALLY BINDING", cost:"Client pays half of arbitrator's fees", limit:"Awards up to $500,000", note:"Best option when client needs a binding, enforceable decision" },
  ];
  return (
    <Reveal delay={160}>
      <SectionLabel color={T.red}>7.12 — Complaints Handling</SectionLabel>
      <Card style={{ background: T.red.bg, border:`1px solid ${T.red.border}` }}>
        <strong style={{ color: T.red.text, fontSize:13 }}>🚒 The Fire Drill Analogy</strong>
        <p style={{ margin:"6px 0 0", fontSize:12, color: T.text, lineHeight:1.7 }}>
          A complaint is the <em>fire alarm</em>. Your personal feelings ('I didn't do it!') are irrelevant.
          Your ONLY job is to follow the mandatory procedure — Recognition → Acknowledgement → Investigation → Substantive Response.
          Hiding complaints, fixing them yourself, or discouraging filing = prohibited practice = career-ending violation.
        </p>
      </Card>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12, marginBottom:16 }}>
        {steps.map((s,i) => (
          <div key={i} style={{ border:`1.5px solid ${s.color.border}`, borderRadius:10, padding:"14px 16px", background: s.color.bg }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <Tag color={s.color}>{s.day}</Tag>
              <span style={{ fontWeight:700, fontSize:13, color: T.text }}>{s.title}</span>
            </div>
            <ul style={{ margin:0, paddingLeft:18 }}>
              {s.items.map((it,j) => (
                <li key={j} style={{ fontSize:11, color: T.text, marginBottom:5, lineHeight:1.6 }}>{it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <strong style={{ fontSize:13, color: T.text, display:"block", marginBottom:10 }}>Resolution Options (After 90-Day Letter)</strong>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
        {resolution.map((r,i) => (
          <div key={i} style={{ border:`1.5px solid ${i===0 ? T.teal.border : T.purple.border}`, borderRadius:10,
            padding:"14px 16px", background: i===0 ? T.teal.bg : T.purple.bg }}>
            <div style={{ fontWeight:800, fontSize:16, color: T.text, marginBottom:4 }}>{r.name}</div>
            <div style={{ fontSize:11, color: T.muted, marginBottom:8 }}>{r.full}</div>
            {[["Binding?",r.binding],["Cost",r.cost],["Max Award",r.limit]].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                <span style={{ color: T.muted }}>{k}:</span>
                <strong style={{ color: T.text }}>{v}</strong>
              </div>
            ))}
            <p style={{ margin:"8px 0 0", fontSize:11, color: T.muted, fontStyle:"italic", lineHeight:1.6 }}>{r.note}</p>
          </div>
        ))}
      </div>

      <Card style={{ border:`1px solid ${T.red.border}`, background: T.red.bg }}>
        <strong style={{ color: T.red.text, fontSize:13 }}>🚫 Prohibited Practices — How to Get Fired</strong>
        <div style={{ marginTop:10, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {[
            ["Off-the-Books Settlement", "Writing personal cheque to client WITHOUT firm's prior written consent = failure to report + concealment"],
            ["Discouraging a Complaint", "'If you file this, it'll ruin our relationship' = intimidation + breach of duty"],
            ["Gag Orders on Misconduct", "Settlement can make $ amount confidential — but CANNOT prevent client from reporting misconduct to CIRO"],
            ["Not Reporting to ComSet", "All non-service complaints MUST be reported to CIRO via ComSet system"],
          ].map(([k,v],i) => (
            <div key={i} style={{ background: T.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:12, fontWeight:700, color: T.red.text, marginBottom:4 }}>✗ {k}</div>
              <div style={{ fontSize:11, color: T.muted, lineHeight:1.5 }}>{v}</div>
            </div>
          ))}
        </div>
      </Card>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   FLASHCARDS
══════════════════════════════════════════════════════════════════════════════ */
const FLASHCARDS = [
  { q:"What is 'Risk Capacity'?", a:"The client's FINANCIAL ABILITY to withstand a loss (math: income, net worth, time horizon). Vetoes Risk Tolerance when in conflict." },
  { q:"What is 'Risk Tolerance'?", a:"The client's EMOTIONAL willingness to watch their money fluctuate. Can be vetoed by low Risk Capacity." },
  { q:"The Golden Rule of risk conflicts?", a:"The overall risk profile = the LOWER of Tolerance vs Capacity. In both conflict scenarios, use the lower." },
  { q:"What is a 'Performance Mismatch'?", a:"A 30% gain on a High-Risk fund for a Low-Risk Income client = SUITABILITY FAILURE. Good performance = meeting objective within risk profile." },
  { q:"What does a 'Negative Screen' mean in ESG?", a:"Excluding 'bad' companies (oil & gas, tobacco, firearms). Creates tracking error vs broad market benchmark." },
  { q:"What is the Superficial Loss Rule?", a:"Loss is disallowed if you (or affiliated person: spouse, RRSP, TFSA, controlled corp) repurchase identical security within 30 days before or after the sale." },
  { q:"What is the OAS Clawback threshold?", a:"Clawback starts ~$91k net income; full OAS lost ~$148k. Any investment that adds taxable income to a borderline client can trigger it." },
  { q:"Why is the TFSA ideal for interest income?", a:"Interest is 100% taxable at marginal rate (worst tax treatment). TFSA shelters it completely, turning 100% taxable income into 0% taxable." },
  { q:"What is Mental Accounting (behavioural bias)?", a:"Treating 'found money' (inheritance) as 'house money' that can be gambled. All money is your money — a loss is a loss regardless of source." },
  { q:"What is Home Country Bias?", a:"Over-investing in your home country due to familiarity. Canada = 3% of world market. A 100% TSX portfolio has no exposure to Apple, Microsoft, Google, or global growth." },
  { q:"What triggers a complaint escalation?", a:"ANY verbal OR written allegation of misconduct: suitability, misrepresentation, unauthorized trading, fraud, errors. Verbal complaints MUST be escalated." },
  { q:"What is OBSI and its limit?", a:"Ombudsman for Banking Services and Investments. FREE for client. Non-binding recommendations up to $350,000. 180 days from firm's substantive response." },
  { q:"What is CIRO Arbitration?", a:"Private legal process. Legally BINDING. Client pays half of arbitrator fees. Awards up to $500,000. Best for clients needing enforceable decision." },
  { q:"What is an 'In-Kind' donation of appreciated securities?", a:"Transferring shares directly to charity eliminates the capital gain (0% inclusion rate) AND earns the full donation receipt. Double tax win." },
  { q:"What is Survivorship Bias?", a:"Only looking at funds that survived (the 'winners') and ignoring thousands that closed. True success rate = winners / ALL funds that started." },
  { q:"What is Loss Aversion?", a:"Losing $100 hurts twice as much as gaining $100 feels good. Causes panic-selling at market bottoms, converting paper losses into permanent losses." },
  { q:"What is the 50% Inclusion Rate?", a:"Canada only taxes 50% of a capital gain. Tax = Capital Gain × 50% × marginal rate. A $10k gain at 40% marginal = $2,000 tax." },
  { q:"What is a Non-Eligible Dividend?", a:"Dividend from a private Canadian company (CCPC) that paid the low small business deduction rate. 15% gross-up, smaller DTC. Higher tax than eligible dividends." },
  { q:"What is the CDA (Capital Dividend Account)?", a:"Notional corporate account tracking the non-taxable 50% of capital gains. Can be paid to owner as tax-free 'capital dividend'. The 'magic' of corporate investing." },
  { q:"What is Confirmation Bias?", a:"Seeking only information that confirms your existing belief while ignoring contradicting evidence. Often causes holding losing positions against professional advice." },
];

function Flashcards() {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [mastered, setMastered] = useState(new Set());
  const remaining = FLASHCARDS.filter((_,i) => !mastered.has(i));

  function next(master) {
    if (master) setMastered(s => new Set([...s, idx]));
    let nextIdx;
    const available = FLASHCARDS.map((_,i) => i).filter(i => !mastered.has(i) && (master ? i !== idx : true));
    if (available.length === 0) { setMastered(new Set()); nextIdx = 0; }
    else nextIdx = available[Math.floor(Math.random() * available.length)];
    setIdx(nextIdx);
    setFlipped(false);
  }

  if (remaining.length === 0) return (
    <Card style={{ textAlign:"center", padding:"32px" }}>
      <div style={{ fontSize:40, marginBottom:8 }}>🏆</div>
      <div style={{ fontFamily:"Georgia,serif", fontSize:18, color: T.text, marginBottom:8 }}>All 20 cards mastered!</div>
      <button onClick={() => { setMastered(new Set()); setIdx(0); setFlipped(false); }}
        style={{ padding:"10px 24px", background: T.blue.text, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700 }}>
        Restart
      </button>
    </Card>
  );

  const card = FLASHCARDS[idx];
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <Tag color={T.blue}>{remaining.length} remaining</Tag>
        <Tag color={T.green}>{mastered.size} mastered</Tag>
      </div>
      <div onClick={() => setFlipped(!flipped)} style={{ cursor:"pointer", perspective:1000 }}>
        <div style={{ minHeight:140, borderRadius:12, border:`2px solid ${flipped ? T.green.border : T.blue.border}`,
          background: flipped ? T.green.bg : T.blue.bg, padding:"24px 28px", display:"flex", alignItems:"center",
          justifyContent:"center", transition:"all 0.3s", position:"relative" }}>
          <div style={{ position:"absolute", top:12, right:14, fontSize:11, color: T.muted, fontWeight:600 }}>
            {flipped ? "ANSWER" : "QUESTION — tap to flip"}
          </div>
          <p style={{ margin:0, fontSize:14, lineHeight:1.7, textAlign:"center", color: T.text,
            fontFamily: flipped ? "system-ui" : "Georgia,serif", fontWeight: flipped ? 400 : 600 }}>
            {flipped ? card.a : card.q}
          </p>
        </div>
      </div>
      {flipped && (
        <div style={{ display:"flex", gap:10, marginTop:12 }}>
          <button onClick={() => next(false)} style={{ flex:1, padding:"10px", borderRadius:8, border:`1.5px solid ${T.red.border}`,
            background: T.red.bg, color: T.red.text, cursor:"pointer", fontWeight:700, fontSize:13 }}>
            ✗ Still Learning
          </button>
          <button onClick={() => next(true)} style={{ flex:1, padding:"10px", borderRadius:8, border:`1.5px solid ${T.green.border}`,
            background: T.green.bg, color: T.green.text, cursor:"pointer", fontWeight:700, fontSize:13 }}>
            ✓ Got It
          </button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   QUIZ
══════════════════════════════════════════════════════════════════════════════ */
const QUESTIONS = [
  { q:"A 30-yr-old engineer is saving $100k for a house down payment needed in 12 MONTHS. He says 'my risk tolerance is Aggressive.' What is the ONLY suitable objective?",
    opts:["Growth — match his tolerance","Income — bonds are safe","Capital Preservation — the 12-month time horizon vetoes his tolerance completely","Speculation — he has time to recover"],
    ans:2, explain:"Risk CAPACITY = zero for this specific goal. A 12-month time horizon for required capital means NO market risk is acceptable, regardless of stated tolerance." },
  { q:"A 60-yr-old 'Balanced' client holds 90% of his $2M portfolio in one inherited family stock and refuses to sell. What is the ONLY compliant RR action?",
    opts:["Sell it anyway — suitability overrides client preferences","Resign from the account","Advise of extreme concentration risk on record, document the informed refusal, manage the other 10%","Change KYC risk profile to 'Speculative' to match the concentration"],
    ans:2, explain:"The RR cannot force a sale. The 'Informed Refusal' process: Advise → Document → Manage what you can." },
  { q:"A client says 'everyone is buying crypto! I can't miss out!' What behavioural bias is this?",
    opts:["Loss Aversion","Anchoring","Herding (FOMO)","Survivorship Bias"],
    ans:2, explain:"Herding = investing because the crowd is doing it, not because of facts. Creates and inflates speculative bubbles (Dot-Com, Crypto 2021)." },
  { q:"An RR makes a trade error losing a client $5,000. He offers the client a personal cheque asking them not to tell the firm. This is:",
    opts:["A compliant resolution — the client was made whole","A Prohibited Practice: off-the-books settlement without firm's written consent","A good retention strategy","Required by CIRO rules"],
    ans:1, explain:"Prohibited Practice. An RR cannot settle complaints with personal funds without prior written approval from the firm. Also constitutes failure to report and concealment of misconduct." },
  { q:"A 65-yr-old client has $130k/year net income. An RR recommends a high-interest bond fund paying 6% ($30k) for their non-registered $500k account. The primary suitability failure is:",
    opts:["The bond fund is too risky for the client","The 6% yield is too low","The recommendation adds $30k taxable income, pushing the client over ~$148k and triggering a 100% OAS Clawback","The client should use an RRSP instead"],
    ans:2, explain:"OAS Clawback is a critical consideration for high-income retirees. Adding taxable interest income can eliminate the entire OAS pension — a net financial loss." },
  { q:"A client sells 'LoserCo' on Dec 1 to harvest a $18k capital loss. On Dec 20 (19 days later), she wants to buy it back. The RR's advice must be:",
    opts:["Go ahead — it's a good value buy","Wait 31 days minimum OR buy a similar (not identical) security instead","Put it in the RRSP and buy it back there","Transfer it to her spouse's account"],
    ans:1, explain:"Superficial Loss Rule: buying identical security within 30 days after sale disallows the loss. The 61-day window is: 30 days before + day of sale + 30 days after." },
  { q:"For a high-income client in the 50% tax bracket with a non-registered account wanting 'Income,' which investment type results in the LOWEST effective tax rate?",
    opts:["Interest from a GIC","Foreign dividends from US stocks","Eligible dividends from Canadian bank stocks","Return of principal from a GIC"],
    ans:2, explain:"Eligible Canadian dividends receive the Dividend Tax Credit (DTC), dramatically lowering the effective rate. Tax efficiency order: Eligible Dividends < Capital Gains < Interest = Foreign Dividends." },
  { q:"A client received the firm's 90-day Substantive Response Letter but is not satisfied and wants a LEGALLY BINDING decision of up to $200,000. Their best option is:",
    opts:["OBSI — free and binding up to $350k","CIRO Arbitration — legally binding, client pays half arbitrator fees, awards up to $500k","File a complaint directly with CIRO Enforcement","Write to the firm's CEO"],
    ans:1, explain:"OBSI is non-binding (firm can refuse to pay). CIRO Arbitration is legally binding and can award up to $500,000 — the correct choice when the client needs enforcement." },
  { q:"A client wants to donate $100k of stock (ACB: $20k, current value: $100k) to a charity. The most tax-efficient strategy is:",
    opts:["Sell stock, pay capital gains tax, then donate cash","Transfer shares IN-KIND directly to the charity — eliminates the $80k capital gain entirely AND earns the full $100k donation receipt","Donate to RRSP first","Put shares in TFSA then donate"],
    ans:1, explain:"'In-Kind' donation of appreciated public securities: capital gain inclusion rate = 0%. You get both the full donation receipt AND avoid the capital gain. Double tax win." },
  { q:"A client has $20k in a non-registered account and $20k in credit card debt at 22% interest. They want to invest in a Balanced Fund (expected 7% return). The most suitable 'best interest' recommendation is:",
    opts:["Buy the Balanced Fund — 7% expected return is good","Pay off the credit card debt first — it provides a guaranteed, risk-free, after-tax 22% return","Split 50/50 between paying debt and investing","Buy a High-Risk fund to potentially earn more than 22%"],
    ans:1, explain:"No suitable investment can guarantee a return higher than the guaranteed COST of 22% credit card interest. Paying off high-interest debt is the undisputed 'best interest' action." },
  { q:"A 'Low Risk / Income' widow asks why her 8% Conservative Fund 'looks bad' versus the 25% S&P 500. The CORRECT RR response is:",
    opts:["Apologize and switch to a 100% stock fund","The 8% is excellent — we met the Income objective and stayed within Low Risk. We cannot compare our safe sedan to the market's Ferrari","Agree it's poor and reduce fees this year","The benchmark should have been 25% all along"],
    ans:1, explain:"'Boring Success.' The RR must reframe: Good performance = meeting the client's specific objective (Income ✓) within their risk profile (Low volatility ✓). The 25% benchmark is the wrong yardstick." },
  { q:"A client says 'I KNEW the market was going to crash — that's why I sold at the bottom.' (But records show they panic-sold.) This is:",
    opts:["Confirmation Bias","Hindsight Bias — claiming to have predicted the outcome AFTER it happened, while actions prove otherwise","Representativeness","Gambler's Fallacy"],
    ans:1, explain:"Hindsight Bias: 'I knew it all along.' Dangerous because it prevents learning from mistakes and increases overconfidence in future bad decisions." },
  { q:"Which asset should be placed INSIDE a TFSA to maximize tax efficiency?",
    opts:["Canadian equity ETF generating capital gains","US stock ETF generating eligible dividends","High-interest bond fund generating fully taxable interest income","Real estate investment generating no income"],
    ans:2, explain:"TFSA shelters your least tax-efficient assets FIRST. Interest income (100% taxable at marginal rate) is worst — putting it in a TFSA converts it to 100% tax-free income." },
  { q:"A firm's Compliance department receives a formal written complaint about unsuitable advice. In addition to handling the client's complaint, the firm MUST:",
    opts:["Immediately fire the RR","Keep it internal to protect client privacy","Report the complaint to CIRO via ComSet (Complaints and Settlement Reporting System)","Offer the client immediate compensation"],
    ans:2, explain:"Mandatory Reporting: All non-service complaints MUST be reported to CIRO via ComSet. This allows CIRO to track RR patterns and conduct enforcement investigations." },
];

function Quiz() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [current, setCurrent] = useState(0);
  const score = Object.entries(answers).filter(([i,a]) => QUESTIONS[+i].ans === a).length;

  function selectAnswer(qi, ai) {
    if (!submitted) setAnswers(prev => ({ ...prev, [qi]: ai }));
  }

  if (submitted) return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:24, marginBottom:20, padding:"20px 24px",
        background: T.card, borderRadius:12, border:`1px solid ${T.border}` }}>
        <ScoreRing score={score} total={QUESTIONS.length} color={score >= 11 ? T.green : score >= 8 ? T.amber : T.red} />
        <div>
          <div style={{ fontFamily:"Georgia,serif", fontSize:20, color: T.text, fontWeight:700 }}>{score}/{QUESTIONS.length} Correct</div>
          <div style={{ fontSize:13, color: T.muted, marginTop:4 }}>
            {score >= 11 ? "Excellent — ready for the RSE exam!" : score >= 8 ? "Good — review flagged areas" : "Keep studying — focus on tax and suitability sections"}
          </div>
          <button onClick={() => { setAnswers({}); setSubmitted(false); setCurrent(0); }}
            style={{ marginTop:10, padding:"6px 16px", background: T.blue.text, color:"#fff", border:"none",
              borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:700 }}>
            Retake
          </button>
        </div>
      </div>
      {QUESTIONS.map((q,i) => {
        const ua = answers[i]; const correct = ua === q.ans;
        return (
          <div key={i} style={{ marginBottom:12, padding:"14px 16px", borderRadius:10,
            background: correct ? T.green.bg : T.red.bg, border:`1.5px solid ${correct ? T.green.border : T.red.border}` }}>
            <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
              <span style={{ fontSize:16 }}>{correct ? "✓" : "✗"}</span>
              <div style={{ flex:1 }}>
                <p style={{ margin:"0 0 6px", fontSize:13, fontWeight:700, color: T.text }}>{q.q}</p>
                {!correct && ua !== undefined && (
                  <p style={{ margin:"0 0 4px", fontSize:12, color: T.red.text }}>Your answer: {q.opts[ua]}</p>
                )}
                <p style={{ margin:"0 0 4px", fontSize:12, color: T.green.text, fontWeight:600 }}>
                  Correct: {q.opts[q.ans]}
                </p>
                <p style={{ margin:0, fontSize:12, color: T.muted, fontStyle:"italic" }}>{q.explain}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const q = QUESTIONS[current];
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <Tag color={T.blue}>Q {current+1} of {QUESTIONS.length}</Tag>
        <span style={{ fontSize:12, color: T.muted }}>{Object.keys(answers).length} answered</span>
      </div>
      <div style={{ background: T.blue.bg, border:`1px solid ${T.blue.border}`, borderRadius:10, padding:"16px 18px", marginBottom:14 }}>
        <p style={{ margin:0, fontSize:14, lineHeight:1.7, color: T.text, fontFamily:"Georgia,serif" }}>{q.q}</p>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
        {q.opts.map((opt,ai) => {
          const selected = answers[current] === ai;
          return (
            <button key={ai} onClick={() => selectAnswer(current, ai)}
              style={{ padding:"12px 16px", borderRadius:8, border:`1.5px solid ${selected ? T.blue.text : T.border}`,
                background: selected ? T.blue.bg : T.card, cursor:"pointer", textAlign:"left",
                fontSize:13, color: T.text, transition:"all 0.15s", lineHeight:1.5 }}>
              <span style={{ fontWeight:700, color: T.blue.text, marginRight:6 }}>{["A","B","C","D"][ai]}.</span>
              {opt}
            </button>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:10 }}>
        {current > 0 && (
          <button onClick={() => setCurrent(c => c-1)} style={{ padding:"9px 18px", borderRadius:8,
            border:`1px solid ${T.border}`, background: T.card, cursor:"pointer", fontSize:13, color: T.muted }}>
            ← Prev
          </button>
        )}
        {current < QUESTIONS.length - 1 ? (
          <button onClick={() => setCurrent(c => c+1)} style={{ padding:"9px 22px", borderRadius:8,
            background: T.blue.text, color:"#fff", border:"none", cursor:"pointer", fontSize:13, fontWeight:700 }}>
            Next →
          </button>
        ) : (
          <button onClick={() => setSubmitted(true)} disabled={Object.keys(answers).length < QUESTIONS.length}
            style={{ padding:"9px 22px", borderRadius:8, background: Object.keys(answers).length < QUESTIONS.length ? T.dim : T.green.text,
              color:"#fff", border:"none", cursor: Object.keys(answers).length < QUESTIONS.length ? "not-allowed" : "pointer", fontSize:13, fontWeight:700 }}>
            Submit Quiz ✓
          </button>
        )}
      </div>
      {Object.keys(answers).length < QUESTIONS.length && current === QUESTIONS.length - 1 && (
        <p style={{ fontSize:11, color: T.red.text, marginTop:8 }}>Answer all {QUESTIONS.length} questions to submit.</p>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   QUICK REFERENCE TABLE
══════════════════════════════════════════════════════════════════════════════ */
const QUICK_REF = [
  { topic:"4 Investment Objectives", detail:"Capital Preservation | Income | Growth | Speculation" },
  { topic:"3 Risk Profile Components", detail:"Tolerance (stomach) | Capacity (wallet) | Need (required return)" },
  { topic:"Golden Rule of Risk Conflicts", detail:"Always assign the LOWER of Tolerance vs Capacity" },
  { topic:"ESG Screens", detail:"Negative = exclude bad. Positive = seek good (Impact Investing)" },
  { topic:"Sharia-Compliant Exclusions", detail:"ALL fixed income (riba) + haram industries (alcohol, gambling, pork, firearms, banks)" },
  { topic:"Informed Refusal Procedure", detail:"Advise → Document → Manage remaining assets" },
  { topic:"Behavioural Biases — Cognitive", detail:"Representativeness | Illusion of Control | Confirmation | Hindsight" },
  { topic:"Behavioural Biases — Emotional", detail:"Loss Aversion | Overconfidence | Endowment" },
  { topic:"Behavioural Biases — Markets", detail:"Survivorship | Gambler's Fallacy | Herding | Home Country" },
  { topic:"Capital Gains Inclusion Rate", detail:"50% of capital gain is taxable. Tax = Gain × 50% × marginal rate" },
  { topic:"Superficial Loss Window", detail:"61 days total: 30 days before + day of sale + 30 days after" },
  { topic:"Capital Loss Carry Rules", detail:"Carry back 3 years (T1A form) | Carry forward indefinitely" },
  { topic:"Tax Efficiency Order (best → worst)", detail:"RoC > Eligible Dividends > Capital Gains > Non-Eligible Dividends > Interest = Foreign Dividends" },
  { topic:"TFSA: Best For", detail:"Shelter highest-taxed assets first (interest, foreign dividends)" },
  { topic:"OAS Clawback", detail:"Starts ~$91k, fully lost ~$148k net income. T-SWP / RoC income avoids triggering it" },
  { topic:"Complaint Timelines", detail:"Acknowledgement: 5 business days | Substantive Response: 90 calendar days" },
  { topic:"OBSI vs Arbitration", detail:"OBSI: free, non-binding, $350k max | Arbitration: binding, $500k max, client pays half" },
  { topic:"Prohibited Practices", detail:"Off-books settlement | Discouraging complaints | Gag orders on misconduct | Not reporting to ComSet" },
  { topic:"In-Kind Donation", detail:"Transfer appreciated shares directly to charity: capital gain = 0% + full donation receipt" },
  { topic:"Emergency Fund Rule", detail:"3-6 months expenses in HISA/MMF required BEFORE any market investment" },
];

function QuickRef() {
  return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead>
          <tr style={{ background: T.blue.bg }}>
            {["Topic","Key Detail"].map(h => (
              <th key={h} style={{ padding:"10px 12px", textAlign:"left", color: T.blue.text, fontWeight:700, borderBottom:`2px solid ${T.blue.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {QUICK_REF.map((r,i) => (
            <tr key={i} style={{ background: i%2===0 ? T.bg : T.card, borderBottom:`1px solid ${T.border}` }}>
              <td style={{ padding:"9px 12px", fontWeight:700, color: T.text, whiteSpace:"nowrap" }}>{r.topic}</td>
              <td style={{ padding:"9px 12px", color: T.muted, lineHeight:1.6 }}>{r.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════════════════════════ */
const TABS = [
  { id:"s1", label:"Holy Trinity", icon:"🎯" },
  { id:"s2", label:"Non-Financial", icon:"🌱" },
  { id:"s3", label:"Behavioural Finance", icon:"🧠" },
  { id:"s4", label:"Suitability", icon:"⚖️" },
  { id:"s5", label:"Tax Planning", icon:"🏦" },
  { id:"s6", label:"Capital Gains", icon:"📈" },
  { id:"s7", label:"Complaints", icon:"🚒" },
  { id:"flash", label:"Flashcards", icon:"🃏" },
  { id:"quiz", label:"Quiz", icon:"✏️" },
  { id:"ref", label:"Quick Ref", icon:"📋" },
];

export default function App() {
  const [active, setActive] = useState("s1");
  const [scrollPct, setScrollPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const pct = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100;
      setScrollPct(pct);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div style={{ fontFamily:"system-ui,-apple-system,sans-serif", background: T.bg, minHeight:"100vh", color: T.text }}>
      {/* Progress Bar */}
      <div style={{ position:"fixed", top:0, left:0, right:0, height:3, background: T.border, zIndex:1000 }}>
        <div style={{ height:"100%", background:`linear-gradient(90deg,${T.blue.text},${T.purple.text})`,
          width:`${scrollPct}%`, transition:"width 0.1s" }} />
      </div>

      {/* Header */}
      <div style={{ background: T.card, borderBottom:`1px solid ${T.border}`, padding:"20px 24px 0", position:"sticky", top:3, zIndex:100 }}>
        <div style={{ maxWidth:900, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"baseline", gap:12, marginBottom:14 }}>
            <span style={{ fontFamily:"Georgia,serif", fontSize:22, fontWeight:700, color: T.text }}>Element 7</span>
            <span style={{ color: T.muted, fontSize:14 }}>Investment Recommendations</span>
            <Tag color={T.blue}>14 Exam Questions</Tag>
          </div>
          <div style={{ display:"flex", gap:2, overflowX:"auto", paddingBottom:0 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => { setActive(t.id); window.scrollTo({ top: 0, behavior:"smooth" }); }}
                style={{ padding:"8px 12px", border:"none", borderBottom:`3px solid ${active===t.id ? T.blue.text : "transparent"}`,
                  background:"none", cursor:"pointer", fontSize:12, fontWeight: active===t.id ? 700 : 500,
                  color: active===t.id ? T.blue.text : T.muted, whiteSpace:"nowrap", transition:"all 0.2s",
                  display:"flex", alignItems:"center", gap:5 }}>
                <span>{t.icon}</span><span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth:900, margin:"0 auto", padding:"28px 20px 60px" }}>
        {active === "s1" && <Section1 />}
        {active === "s2" && <Section2 />}
        {active === "s3" && <Section3 />}
        {active === "s4" && <Section4 />}
        {active === "s5" && <Section5 />}
        {active === "s6" && <Section6 />}
        {active === "s7" && <Section7 />}
        {active === "flash" && (
          <Reveal><SectionLabel color={T.teal}>Flashcards — 20 Cards</SectionLabel><Flashcards /></Reveal>
        )}
        {active === "quiz" && (
          <Reveal><SectionLabel color={T.orange}>Practice Quiz — 14 Questions</SectionLabel><Quiz /></Reveal>
        )}
        {active === "ref" && (
          <Reveal><SectionLabel color={T.blue}>Quick Reference — 20 Key Facts</SectionLabel><QuickRef /></Reveal>
        )}
      </div>
    </div>
  );
}
